<script setup>
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@/stores/authStores'
import { useMediaStore } from "@/stores/mediaStore"
import { useICPStore } from "@/stores/icpStore";
import { keycloak } from '@/main'
import AdminManager from "@/components/leftToolbar/adminManager/AdminManager.vue";
import axios from "axios";
import { notifyError } from "@/services/toastService";
import ServerDetailsDialog from "@/components/leftToolbar/ServerDetailsDialog.vue";

// General
const authStore = useAuthStore()
const clientRoles = keycloak.tokenParsed.resource_access["cairo-metro4-api"].roles;
const currentTime = ref('00:00:00')
const showDialog = ref(false)
const selectedServer = ref('')

function openServerDialog(name) {
  selectedServer.value = name
  showDialog.value = true
}

const currentServer = computed(() => {
  return window.location.hostname.replace(/^(www\.|metrocairo4-)/, '').split('.')[0];
})
//const currentServer = "iside"

const isideStatus = ref('')
const osirideStatus = ref('')
const healthFailureCount = ref(0)
const lteStatus = computed(() => {
  return useICPStore().eventData.status === "0" ? "online" : "offline"
})

// eslint-disable-next-line
const appVersion = __APP_VERSION__;

function updateTime() {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString()
}

function checkBackendStatus() {
  axios.get('https://metrocairo4-iside-api/actuator/health', { withCredentials: false })
      .then(response => {
        isideStatus.value = getStatus(response.data);
        if (currentServer.value === "iside") {
          healthFailureCount.value = 0
        }
      })
      .catch(error => {
        const status = error?.response?.status ?? null;
        if (currentServer.value === "iside") {
          healthFailureCount.value += 1;
          if (healthFailureCount.value >= 3) {
            // Redirect to the other server
            notifyError("Current server is down. Redirecting to the other server.", {
              onClose: () => {
                window.location.href = currentServer.value === "iside"
                    ? window.location.href.replace("iside", "osiride")
                    : window.location.href.replace("osiride", "iside");
              }
            });
          }
        }
        if (status === 503) {
          isideStatus.value = getStatus(error.response.data);
        } else {
          isideStatus.value = {
            fullStatus: "Offline",
            message: 'Server not reachable'
          };
        }
        if (currentServer.value === "iside") {
          isideStatus.value.message += " (\n Switching to backup server after " + healthFailureCount.value + "/3 failed attempts)";
        }
      })

  axios.get('https://metrocairo4-osiride-api/actuator/health', { withCredentials: false })
      .then(response => {
        osirideStatus.value = getStatus(response.data)
        if (currentServer.value === "osiride") {
          healthFailureCount.value = 0;
        }
      })
      .catch(error => {
        const status = error?.response?.status ?? null;
        if (currentServer.value === "osiride") {
          healthFailureCount.value += 1;
          if (healthFailureCount.value >= 3) {
            // Redirect to the other server
            notifyError("Current server is down. Redirecting to the other server.", {
              onClose: () => {
                window.location.href = currentServer.value === "iside"
                    ? window.location.href.replace("iside", "osiride")
                    : window.location.href.replace("osiride", "iside");
              }
            });
          }
        }
        if (status === 503) {
          osirideStatus.value = getStatus(error.response.data);
        } else {
          osirideStatus.value = {
            fullStatus: "Offline",
            message: 'Server not reachable'
          };
        }
        if (currentServer.value === "osiride") {
          osirideStatus.value.message += " \n switching to backup server after " + healthFailureCount.value + "/3 failed attempts.";
        }
      })
}

function getStatus(status) {
  const serverStatus = status.status;
  const dbStatus = status.components.db.status;
  const mqttStatus = status.components.mqttHealth.status;

  const fullStatus = serverStatus === 'UP' ? "Online" : "Offline";
  const message = `Server Status: ${serverStatus}\n - Database: ${dbStatus}\n, MQTT: ${mqttStatus}`;

  return { fullStatus, message };
}

onMounted(() => {
  updateTime()
  setInterval(updateTime, 1000)

  checkBackendStatus()
  setInterval(checkBackendStatus, 15000)
})

const logout = () => {
  authStore.logout()
}
</script>

<template>
  <div class="toolbar-bottom">
    <!-- Volume Slider -->
    <div class="mb-2 d-flex align-center pa-2">
      <v-icon class="mr-2" size="18">mdi-volume-high</v-icon>
      <v-slider
          v-model="useMediaStore().showValue"
          hide-details
          max="100"
          min="0"
          step="1"
          style="width: 90px; z-index:999"
          @end="useMediaStore().updateVolume"
      />
    </div>

    <!-- Backend Status Icons -->
    <v-card class="mb-1 d-flex justify-center align-center" width="100%">
      <!-- ISIDE -->
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-icon
              v-bind="props"
              :color="isideStatus.fullStatus === 'Online' ? 'green' : (isideStatus.fullStatus === 'Offline' ? 'red' : 'grey')"
              :style="currentServer?.trim() === 'iside'
              ? {
                  boxShadow: isideStatus.fullStatus === 'Online'
                    ? '0 0 20px 6px rgb(3 227 3 / 85%)'
                    : '0 0 20px 6px rgb(200,0,0,0.85)'
                }
              : {}"
              class="mr-3"
              size="36"
              @click="openServerDialog('iside')"
          >
            mdi-server
          </v-icon>
        </template>
        <span>
          OCC server: {{ isideStatus.message }}
        </span>
      </v-tooltip>

      <!-- OSIRIDE -->
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-icon
              v-bind="props"
              :color="osirideStatus.fullStatus === 'Online' ? 'green' : (osirideStatus.fullStatus === 'Offline' ? 'red' : 'grey')"
              :style="currentServer && currentServer.trim() === 'osiride'
              ? (osirideStatus.fullStatus === 'Online'
                  ? 'box-shadow: 0 0 20px 6px rgb(3 227 3 / 85%);'
                  : 'box-shadow: 0 0 20px 6px rgb(200,0,0,0.85);')
              : ''"
              class="mr-3"
              size="36"
              @click="openServerDialog('osiride')"
          >
            mdi-server
          </v-icon>
        </template>
        <span>
          BOCC server: {{ osirideStatus.message }}
        </span>
      </v-tooltip>

      <!-- LTE status -->
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-icon
              :color="lteStatus === 'online' ? 'green' : 'red'"
              class="mb-2"
              size="36"
              v-bind="props"
          >
            mdi-access-point
          </v-icon>
        </template>
        <span>
          Last notification: {{ useICPStore().eventData.desc }}
        </span>
      </v-tooltip>
    </v-card>

    <!-- Operator Info -->
    <v-card class="pa-1 mb-1" elevation="0" width="100%">
      <div class="text-caption text-center align-self-center font-weight-bold">
        {{ useAuthStore().loggedUserProfile }}
        <div>
          {{ useAuthStore().loggedUser }}
        </div>
      </div>
    </v-card>

    <!-- Logos -->
    <div class="d-flex justify-center align-center ma-2 logos-container">
      <v-img
          alt="Hitachi Logo"
          class="mx-2 logo-img"
          src="/hitachi-logo.png"
      />
      <v-img
          alt="NAT Logo"
          class="mx-2 logo-img"
          src="/NAT_logo.png"
      />
    </div>

    <!-- Admin management tools -->
    <AdminManager v-if="clientRoles.includes('Admin')" />

    <!-- Logout Button -->
    <div class="w-100 mb-2">
      <v-btn block class="mt-0" type="button" @click="logout()">Logout</v-btn>
    </div>

    <!-- Clock -->
    <v-card
        class="pa-1 text-center align-self-center"
        color="blue-grey lighten-4"
        dark
        height="48"
        width="100%"
    >
      <div>{{ currentTime }}</div>
      <div class="version-text">v{{ appVersion }}</div>
    </v-card>
  </div>

  <ServerDetailsDialog
      v-model="showDialog"
      :serverName="selectedServer"
  />
</template>

<style scoped>
.v-navigation-drawer {
  border-right: 1px solid #ccc;
}

.v-card {
  border-radius: 0;
  box-shadow: none;
}

.logos-container {
  width: 100%;
  margin: 0px;
}

.logo-img {
  height: auto;
  object-fit: contain;
}

.version-text {
  font-size: 0.8rem;
  opacity: 0.7;
}
</style>
